import type { HomePage } from './homeNavigation';

/**
 * 后端User对象接口（匹配后端Java实体）
 */
export interface BackendUser {
  homePage?: HomePage;
  id?: number;
  userCode?: string;
  username?: string;
  usernameEn?: string;
  name?: string;
  email?: string;
  phone?: string;
  avatar?: string;
  signature?: string;
  title?: string;
  department?: string;
  deptId?: number;
  userGroup?: string;
  country?: string;
  address?: string;
  access?: string;
  role?: string; // CurrentUserDto 使用 role 传递账号访问级别
  status?: number;
  remark?: string;
  createTime?: string;
  updateTime?: string;
  creator?: string;
  permissions?: string[]; // 后端返回的权限码列表
}

/**
 * 前端CurrentUser格式
 */
export interface CurrentUser {
  homePage?: HomePage;
  name?: string;
  avatar?: string;
  userid?: string;
  email?: string;
  signature?: string;
  title?: string;
  group?: string;
  tags?: { key?: string; label?: string }[];
  notifyCount?: number;
  unreadCount?: number;
  country?: string;
  access?: string;
  geographic?: {
    province?: { label?: string; key?: string };
    city?: { label?: string; key?: string };
  };
  address?: string;
  phone?: string;
  permissions?: string[]; // 权限码列表，用于access.ts细粒度权限控制
}

/**
 * 将后端User对象转换为前端CurrentUser格式
 * @param backendUser 后端返回的User对象
 * @returns 前端期望的CurrentUser对象
 */
export function convertBackendUserToCurrentUser(
  backendUser: BackendUser | null | undefined,
): CurrentUser | undefined {
  if (!backendUser) {
    return undefined;
  }

  return {
    ...(backendUser.homePage ? { homePage: backendUser.homePage } : {}),
    name: backendUser.name || backendUser.username,
    avatar: backendUser.avatar,
    userid: backendUser.id?.toString(),
    email: backendUser.email,
    signature: backendUser.signature,
    title: backendUser.title,
    group: backendUser.userGroup || backendUser.department,
    country: backendUser.country,
    access: backendUser.access || backendUser.role,
    address: backendUser.address,
    phone: backendUser.phone,
    permissions: Array.isArray(backendUser.permissions) ? backendUser.permissions : [],
  };
}

/**
 * 转换已鉴权的 /api/currentUser 响应。
 * 该接口必须携带权限数组；空用户或不完整数据不得降级为空权限登录。
 */
export function convertAuthoritativeBackendUserToCurrentUser(
  backendUser: BackendUser | null | undefined,
): CurrentUser {
  if (!backendUser || !Array.isArray(backendUser.permissions)) {
    throw new Error('currentUser permissions payload is invalid');
  }

  return convertBackendUserToCurrentUser(backendUser) as CurrentUser;
}
